/**
 * Main App component with routing
 */

import { BrowserRouter, Routes, Route, Link } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { HomePage } from './pages/HomePage';
import { DanceDetailPage } from './pages/DanceDetailPage';
import { EditDancePage } from './pages/EditDancePage';
import { LoginPage } from './pages/LoginPage';
import { RegisterPage } from './pages/RegisterPage';
import { ProgramListPage } from './pages/ProgramListPage';
import { ProgramBuilderPage } from './pages/ProgramBuilderPage';
import { ProgramDetailPage } from './pages/ProgramDetailPage';
import { PublicProgramPage } from './pages/PublicProgramPage';
import { GenerateProgramPage } from './pages/GenerateProgramPage';
import { DanceUploadForm } from './components/DanceUploadForm';
import { AIPromptForm } from './components/AIPromptForm';
import { DanceList } from './components/DanceList';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function Header() {
  const { isAuthenticated, user, logout } = useAuth();

  return (
    <header style={{
      background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
      color: 'white',
      padding: '14px 24px',
      marginBottom: '0',
      boxShadow: '0 2px 12px rgba(124,58,237,0.18)'
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        maxWidth: '1200px',
        margin: '0 auto'
      }}>
        <Link to="/" style={{ color: 'white', textDecoration: 'none', fontSize: '22px', fontWeight: '800', letterSpacing: '-0.3px' }}>
          💃 Dance Transcription
        </Link>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', fontSize: '0.95rem' }}>
          {isAuthenticated ? (
            <>
              <Link to="/programs" style={{ color: 'rgba(255,255,255,0.9)', textDecoration: 'none', fontWeight: '600' }}>My Programs</Link>
              <span style={{ color: 'rgba(255,255,255,0.75)' }}>Hi, {user?.displayName}</span>
              <button
                onClick={logout}
                style={{
                  padding: '7px 18px',
                  backgroundColor: 'rgba(255,255,255,0.15)',
                  color: 'white',
                  border: '1.5px solid rgba(255,255,255,0.4)',
                  borderRadius: '20px',
                  cursor: 'pointer',
                  fontWeight: '700',
                  fontFamily: 'inherit',
                  fontSize: '0.9rem',
                  transition: 'all 0.18s ease'
                }}
              >
                Logout
              </button>
            </>
          ) : (
            <Link
              to="/login"
              style={{
                padding: '7px 18px',
                backgroundColor: 'rgba(255,255,255,0.15)',
                color: 'white',
                textDecoration: 'none',
                borderRadius: '20px',
                border: '1.5px solid rgba(255,255,255,0.4)',
                fontWeight: '700',
                fontSize: '0.9rem'
              }}
            >
              Login
            </Link>
          )}
        </div>
      </div>
    </header>
  );
}

function AppContent() {
  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#faf9ff' }}>
      <Header />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/generate" element={<AIPromptForm />} />
        <Route path="/upload" element={<DanceUploadForm />} />
        <Route path="/browse" element={<DanceList />} />
        <Route path="/dances/:danceId" element={<DanceDetailPage />} />
        <Route path="/dances/:danceId/edit" element={<EditDancePage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/programs" element={<ProgramListPage />} />
        <Route path="/programs/generate" element={<GenerateProgramPage />} />
        <Route path="/programs/new" element={<ProgramBuilderPage />} />
        <Route path="/programs/:programId" element={<ProgramDetailPage />} />
        <Route path="/programs/:programId/edit" element={<ProgramBuilderPage />} />
        <Route path="/programs/public/:publicId" element={<PublicProgramPage />} />
      </Routes>
    </div>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

export default App;
